import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchApi,
  makeResetCache,
  notify,
  notifyResponse,
  PageTitle,
  useCacheRecord,
  selectUser,
  UserState,
} from 'gossamer-front-end';
import {
  AdminUpdateEventRequest,
  RecurringEventEditFields,
  RecurringEventEditScope,
  Event,
  EventExtendedView,
  EventExtendedViewRow,
  Ticket,
} from 'cb-model';
import { Button } from 'react-bootstrap';
import { EventForm } from './form/EventForm';
import { asUpdateRequest, convertEventToCreateRequest } from './form/eventFormUtils';
import { CustomInfo, PERSONAL_INSTITUTION_ID } from 'cb-unified';
import { EventRecurringEditModal } from './repeat/EventRecurringEditModal';

export const EventEdit = (): React.JSX.Element => {
  const params = useParams<{ key: string }>();
  const eventId = params.key ? Number(params.key) : null;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const resetCache = makeResetCache(dispatch);

  const user = useSelector((state: { user: UserState }) => selectUser(state));

  const record = useCacheRecord<EventExtendedViewRow>(EventExtendedView, params.key, !params.key);

  const [submitting, setSubmitting] = React.useState(false);
  const [value, setValue] = React.useState<AdminUpdateEventRequest | null>(null);
  const [recurringScope, setRecurringScope] = React.useState<RecurringEventEditScope | null>(null);

  React.useEffect(() => {
    if (!record.data || !eventId) return;
    const createReq = convertEventToCreateRequest(record.data);
    setValue(asUpdateRequest(eventId, createReq));
  }, [record.data, eventId]);

  const handleSubmit = async () => {
    if (!value || !eventId || submitting) return;
    setSubmitting(true);
    try {
      const payload: AdminUpdateEventRequest = { ...value, event_id: eventId };
      if (payload.type === 'on-demand') {
        delete (payload as any).start_date;
        delete (payload as any).end_date;
      }
      const response = await fetchApi('POST', `/main/event-update`, payload);
      notifyResponse(response);
      resetCache(Event.id, String(eventId));
      resetCache(EventExtendedView.id, String(eventId));
      resetCache(Ticket.id, String(eventId));
      navigate(`/admin/event/display/${eventId}`);
    } catch (e) {
      console.error(e);
      notify(['Could not update event', 'danger']);
    }
    setSubmitting(false);
  };

  const handleRecurringApplied = () => {
    if (!eventId) return;
    resetCache(Event.id);
    resetCache(EventExtendedView.id);
    resetCache(Event.id, String(eventId));
    resetCache(EventExtendedView.id, String(eventId));
    resetCache(Ticket.id, String(eventId));
    navigate(`/admin/event/display/${eventId}`);
  };

  const recurringFields: RecurringEventEditFields | undefined = React.useMemo(() => {
    if (!value) return undefined;
    const { event_id, tickets, repetition, start_date, end_date, ...proposedFields } = value;
    return proposedFields;
  }, [value]);
  const isRepeated = !!record.data?.repeat_group_id;

  const institutionId =
    record.data?.institution_id ??
    (user.customInfo as CustomInfo | undefined)?.createEventInstitutionId ??
    PERSONAL_INSTITUTION_ID;

  return (
    <>
      <PageTitle title="Edit Event" subtitle={params.key ? `Event ${params.key}` : ''} />
      {!value && <div>Loading event…</div>}
      {value && (
        <>
          <EventForm
            submitLabel="Save changes"
            submitting={submitting}
            value={value}
            institutionId={institutionId}
            isUpdate={true}
            onChange={setValue as any}
            onSubmit={handleSubmit}
            onCancel={() => navigate(`/admin/event/display/${eventId}`)}
            submitActions={
              isRepeated ? (
                <>
                  <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                    Save this event
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setRecurringScope('this-and-future')}
                    disabled={submitting}
                  >
                    Save this and future events
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setRecurringScope('entire-series')}
                    disabled={submitting}
                  >
                    Save entire series
                  </Button>
                </>
              ) : undefined
            }
          />
          <div className="mt-3" />
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/admin/event/display/${eventId}`)}>
            Back to event
          </Button>
        </>
      )}
      {value && recurringFields && eventId && recurringScope && (
        <EventRecurringEditModal
          eventId={eventId}
          scope={recurringScope}
          proposedFields={recurringFields}
          submittedStartDate={value.start_date}
          submittedEndDate={value.end_date}
          show={true}
          onHide={() => setRecurringScope(null)}
          onApplied={handleRecurringApplied}
        />
      )}
    </>
  );
};
